import { Box, Center, Flex, HStack, Text, Tooltip } from '@chakra-ui/react'
import { generatePath } from 'react-router'
// eslint-disable-next-line no-restricted-imports -- 新しいアイコンに移行する
import { Down, Up } from 'src/components/__legacy__/icon/monochrome'
import { MessageWarning } from 'src/components/icon'
import { LinkTable, LinkTableColumn } from 'src/components/LinkTable/LinkTable'
import { MemberLabel } from 'src/components/MemberLabel/MemberLabel'
import { PartnerBadge } from 'src/components/PartnerBadge/PartnerBadge'
import { Paths } from 'src/constants/paths'
import {
  useAuthenticatedAccount,
  usePermission,
} from 'src/features/auth/context'
import { PERMISSIONS } from 'src/lib/permission'
import { assertNever } from 'src/utils/assertNever'
import { formatDate } from 'src/utils/formatDate'
import { getFullName } from 'src/utils/getFullName'

import { useExplanations } from '../../api'
import {
  ExplanationSortField,
  useSortExplanations,
} from '../../hooks/useSortExplanations'
import { ExplanationRevisionMember } from '../../types'
import { ExplanationStatusBadge } from '../ExplanationStatusBadge/ExplanationStatusBadge'

const tableFields = [
  'isRequiredReAgreement',
  'status',
  'candidateId',
  'hospitalName',
  'diseaseId',
  'member',
  'agreementCompletedAt',
  'deliveredAt',
] as const

type ExplanationTableField = (typeof tableFields)[number]

const getHeaderText = (field: ExplanationTableField): string | null => {
  switch (field) {
    case 'isRequiredReAgreement':
      return null
    case 'status':
      return 'ステータス'
    case 'candidateId':
      return '候補ID'
    case 'hospitalName':
      return '医療機関'
    case 'diseaseId':
      return '症例番号'
    case 'member':
      return '担当者'
    case 'agreementCompletedAt':
      return '同意完了日'
    case 'deliveredAt':
      return '交付日'
    default:
      return assertNever(field)
  }
}

const getColumns = (param: {
  isVisibleTrialHospital: boolean
  sortField: ExplanationSortField | undefined
  sortOrder: 'asc' | 'desc' | undefined
}): LinkTableColumn<ExplanationTableField>[] => {
  return tableFields.map(fieldKey => {
    const isVisible =
      fieldKey === 'hospitalName' ? param.isVisibleTrialHospital : true

    return {
      key: fieldKey,
      isVisible,
      thProps: fieldKey === 'isRequiredReAgreement' ? { p: '0' } : {},
      renderHeader: (
        <HStack spacing="2">
          <Text as="span">{getHeaderText(fieldKey)}</Text>
          {fieldKey === param.sortField &&
            (param.sortOrder === 'desc' ? <Down /> : <Up />)}
        </HStack>
      ),
    }
  })
}

export const ExplanationsTable = () => {
  const {
    account: { selectedTrial, selectedTrialHospitalUid },
  } = useAuthenticatedAccount()

  const { field: sortField, order: sortOrder } = useSortExplanations()

  const { data } = useExplanations()
  const { hasPermission } = usePermission()

  const explanations = data?.explanations ?? []

  return (
    <LinkTable
      data={explanations}
      columns={getColumns({
        isVisibleTrialHospital: hasPermission(PERMISSIONS.Hospital_BelongAll),
        sortField,
        sortOrder,
      })}
      linkTarget="candidateId"
      link={explanation =>
        generatePath(Paths.Explanation, {
          trialUid: selectedTrial.uid,
          explanationUid: explanation.uid,
        })
      }
      renderRow={explanation => {
        const latestRevision = explanation.latestRevision
        return {
          rowId: explanation.uid,
          columnValues: {
            isRequiredReAgreement: {
              renderColumn: explanation.isRequiredReAgreement ? (
                <Box pos="relative" zIndex="1" mr="2">
                  <Tooltip label="再同意が必要です">
                    <Center color="yellow.500" as="span" minW="12px">
                      <MessageWarning size="12px" />
                    </Center>
                  </Tooltip>
                </Box>
              ) : null,
              tdProps: { p: '0' },
            },
            status: {
              renderColumn: (
                <ExplanationStatusBadge status={latestRevision.status} />
              ),
              tdProps: { minW: '120px' },
            },
            candidateId: {
              renderColumn: (
                <HStack as="span">
                  <Text as="span">{explanation.patient.candidateId}</Text>
                  {latestRevision.type === 'RemotePartner' &&
                    (explanation.trialHospital.uid ===
                      selectedTrialHospitalUid ||
                      !selectedTrialHospitalUid) && <PartnerBadge />}
                </HStack>
              ),
              tdProps: { minW: '160px' },
            },
            hospitalName: {
              renderColumn: explanation.trialHospital.name,
            },
            diseaseId: {
              renderColumn: explanation.patient.expDiseaseId,
              tdProps: { minW: '160px' },
            },
            member: {
              renderColumn: <MembersCell members={latestRevision.members} />,
              tdProps: {
                maxW: '240px',
              },
            },
            agreementCompletedAt: {
              renderColumn:
                explanation.agreementCompletedAt !== undefined
                  ? formatDate(
                      explanation.agreementCompletedAt,
                      'YYYY/MM/DD (ddd)',
                    )
                  : '',
              tdProps: { minW: '160px' },
            },
            deliveredAt: {
              renderColumn: latestRevision.deliveredAt
                ? formatDate(latestRevision.deliveredAt, 'YYYY/MM/DD (ddd)')
                : '',
              tdProps: { minW: '160px' },
            },
          },
        }
      }}
    />
  )
}

const MembersCell = ({ members }: { members: ExplanationRevisionMember[] }) => {
  const DISPLAY_COUNT = 4

  const overflowCount = members.length - DISPLAY_COUNT

  return (
    <Box>
      <Flex gap={4} wrap="wrap" columnGap={4}>
        {members
          .sort((a, b) => a.index - b.index)
          .slice(0, DISPLAY_COUNT)
          .map(({ trialMember, isPartner }) => (
            <MemberLabel
              key={trialMember.uid}
              role={trialMember.role}
              displayName={getFullName(trialMember)}
              isPartner={isPartner}
            />
          ))}
        {overflowCount > 0 && <Text as="span">他{overflowCount}名</Text>}
      </Flex>
    </Box>
  )
}
